import React from 'react';
import ProjectIcon from './ProjectIcon';
import { Check } from 'lucide-react';

/**
 * Reusable multi-select picker for projects.
 * Renders a chip-style toggleable list — clicking a chip toggles its id in
 * the `selectedIds` array via `onChange(newIds)`.
 *
 * Props:
 *   projects:    Project[]   — full project list
 *   selectedIds: string[]    — currently selected project ids
 *   onChange:    (ids) => void
 *   testIdPrefix: optional string for data-testid namespacing
 */
export default function ProjectMultiSelect({ projects = [], selectedIds = [], onChange, testIdPrefix = 'project-picker' }) {
  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(x => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (!projects.length) {
    return (
      <p className="text-xs text-gray-500 italic">Henüz proje yok. Önce proje oluşturun.</p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid={testIdPrefix}>
      {projects.map(project => {
        const selected = selectedIds.includes(project.id);
        return (
          <button
            key={project.id}
            type="button"
            onClick={() => toggle(project.id)}
            data-testid={`${testIdPrefix}-${project.id}`}
            className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border text-sm transition-all ${
              selected
                ? 'bg-blue-50 border-blue-400 text-blue-800 shadow-sm'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
            }`}
            title={project.name}
          >
            <ProjectIcon project={project} size="xs" />
            <span className="truncate max-w-[160px]">{project.name}</span>
            {selected && <Check className="w-3.5 h-3.5 text-blue-600" />}
          </button>
        );
      })}
    </div>
  );
}
